import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'
import type {
  CortexChatMessage,
  CortexSession,
  CortexSessionMessage,
  CortexStreamEvent,
} from '../services/api'
import AriaSessionsSidebar from '../components/AriaSessionsSidebar'
import MarkdownMessage from '../components/MarkdownMessage'
import './CortexPage.css'
import { scopedLocalStorage } from '../lib/storage'

interface Organization {
  id: number
  name: string
  slug: string
  member_role: string
}

// TQ #8504: Die Aria-Seite ist wieder reiner Chat. Das frühere Zahnrad-Icon
// (oben rechts) und die dahinterliegenden Settings-Sub-Tabs (Meine Tokens,
// Persona, Org-Defaults, Datenschutz, GroupFolders, Audit) sind entfernt — alle
// Aria-Einstellungen leben jetzt im "Einstellungen"-Bereich mit Tab-Leiste
// (Settings.tsx, Hub-Muster). Vorher TQ #6612/#6298 (Zahnrad-Flow).

interface CortexPageProps {
  organization: Organization
}

// TQ #6981 (Confirm-Engine P3): API-gestuetzte Confirm-Anfrage aus dem
// `confirmation_required` Stream-Event; ruft /confirmations/{id}/approve|cancel.
// (Die Legacy-[ARIA_PREVIEW]-Prose-Card wurde in P5/#6982 entfernt.)
interface PendingConfirm {
  tool: string
  reason: string
  instruction: string
  confirmId: string
}

interface Message {
  role: 'user' | 'assistant'
  content: string
  // P3-Confirm-Card State (deterministische Confirm-Engine, TQ #6981/#6982).
  pendingConfirm?: PendingConfirm
  confirmState?: 'pending' | 'approving' | 'approved' | 'cancelled' | 'error'
  confirmError?: string
}

const STORAGE_KEY_CURRENT = 'aria_session_id_org_page'

const KNOWN_ROLES: ReadonlyArray<CortexChatMessage['role']> = ['user', 'assistant']

function isDisplayableRole(role: CortexSessionMessage['role']): role is CortexChatMessage['role'] {
  return (KNOWN_ROLES as ReadonlyArray<string>).includes(role)
}

// TQ #6982 (P5): parsePreview() + PreviewCard (Legacy-[ARIA_PREVIEW]-Prosa-Confirm)
// entfernt. Write-Confirms laufen ausschliesslich ueber die deterministische
// ConfirmCard (confirmation_required-Event). Die geteilten .aria-preview-* CSS-
// Klassen bleiben (ConfirmCard nutzt sie weiter).

// TQ #6981: Confirm-Card fuer das `confirmation_required` Event. Reused die
// `.aria-preview-*` Styles (gleiche Optik wie die Legacy-Preview-Card), ruft
// aber den echten approve/cancel-Endpoint. Tool + reason kommen vom Server.
interface ConfirmCardProps {
  confirm: PendingConfirm
  state: NonNullable<Message['confirmState']>
  error?: string
  onApprove: () => void
  onCancel: () => void
}

function ConfirmCard({ confirm, state, error, onApprove, onCancel }: ConfirmCardProps) {
  if (state === 'approved') {
    return (
      <div className="aria-preview-result aria-preview-result--confirmed">
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Aktion bestätigt und ausgeführt.
      </div>
    )
  }
  if (state === 'cancelled') {
    return (
      <div className="aria-preview-result aria-preview-result--cancelled">
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
        Abgebrochen
      </div>
    )
  }
  const busy = state === 'approving'
  return (
    <div className="aria-preview-card aria-confirm-card">
      <div className="aria-preview-card-header">
        <div className="aria-preview-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <span className="aria-preview-card-title">Bestätigung erforderlich</span>
      </div>
      <ul className="aria-preview-details">
        <li><strong>Tool:</strong> <code>{confirm.tool}</code></li>
        {confirm.reason && <li>{confirm.reason}</li>}
      </ul>
      {confirm.instruction && <p className="aria-preview-question">{confirm.instruction}</p>}
      {state === 'error' && error && (
        <p className="aria-confirm-error">{error}</p>
      )}
      <div className="aria-preview-actions">
        <button className="aria-preview-btn aria-preview-btn--cancel" onClick={onCancel} disabled={busy}>
          Abbrechen
        </button>
        <button className="aria-preview-btn aria-preview-btn--confirm" onClick={onApprove} disabled={busy}>
          {busy ? 'Wird ausgeführt…' : 'Bestätigen & ausführen'}
        </button>
      </div>
    </div>
  )
}

export default function CortexPage({ organization }: CortexPageProps) {
  const { token } = useAuth()

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true)

  // Sessions
  const [sessions, setSessions] = useState<CortexSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [currentSessionId, setCurrentSessionIdState] = useState<string | undefined>(undefined)

  // Chat state
  const [messages, setMessages] = useState<Message[]>([])
  const [restoring, setRestoring] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // TQ #6093 Streaming-State: ein dynamisches Status-Feld + die laufende
  // Assistant-Bubble (siehe analog AriaChatPanel). Verhindert mehrere leere
  // 'A'-Avatar-Bubbles waehrend Aria Tools sucht/aufruft.
  const [toolStatus, setToolStatus] = useState<string | null>(null)
  const [streamingContent, setStreamingContent] = useState<string>('')
  const streamingContentRef = useRef<string>('')
  const streamAbortRef = useRef<(() => void) | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const initRef = useRef(false)

  // TQ #6971: Storage-Key pro Tenant-Slug — der globale Key liess die "aktuelle
  // Session" ueber Tenant-Kontexte hinweg kollidieren. Pro Org eigener Pointer.
  const storageKey = `${STORAGE_KEY_CURRENT}::${organization.slug}`

  const setCurrentSessionId = useCallback((id: string | undefined) => {
    setCurrentSessionIdState(id)
    try {
      if (id) scopedLocalStorage.setItem(storageKey, id)
      else scopedLocalStorage.removeItem(storageKey)
    } catch {/* ignore */}
  }, [storageKey])

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`
    }
  }, [input])

  // ─── Sessions API ────────────────────────────────────────────
  // TQ #6971: Rueckgabe null = transienter Fehler (vs. [] = wirklich keine
  // Sessions). Der Init-Effect darf bei Fehler den gespeicherten Pointer NICHT
  // wegwerfen, sonst geht der Verlauf bei einem kurzen List-Fehler verloren.
  const loadSessions = useCallback(async (): Promise<CortexSession[] | null> => {
    if (!token) return null
    api.setToken(token)
    setSessionsLoading(true)
    setSessionsError(null)
    try {
      const list = await api.listCortexSessions({ limit: 100 })
      list.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime())
      setSessions(list)
      return list
    } catch (err) {
      console.error('[Aria/Cortex] Failed to list sessions:', err)
      setSessionsError('Sessions konnten nicht geladen werden.')
      return null
    } finally {
      setSessionsLoading(false)
    }
  }, [token])

  // TQ #6971: gibt true zurueck wenn die History geladen wurde, false wenn die
  // Session weg ist / ein Fehler auftrat — der Init-Effect nutzt das fuer den
  // Fallback auf die neueste Session.
  const loadSessionMessages = useCallback(async (sessionId: string): Promise<boolean> => {
    setRestoring(true)
    setRestoreError(null)
    setMessages([])
    try {
      const result = await api.getCortexSessionMessages(sessionId)
      if (result === null) {
        setRestoreError('Diese Unterhaltung existiert nicht mehr.')
        return false
      }
      // TQ #6093: leere Assistant-Bubbles (intermediate tool-call iterations
      // ohne text content) sind UX-Laerm. Cortex persistiert sie aus
      // Audit-Gruenden, der User soll sie aber nicht als 'A'-Avatar sehen.
      const restored: Message[] = result
        .filter(m => isDisplayableRole(m.role))
        .map(m => ({ role: m.role as Message['role'], content: m.content }))
        .filter(m => m.role === 'user' || m.content.trim().length > 0)
      setMessages(restored)
      return true
    } catch (err) {
      console.error('[Aria/Cortex] Failed to load messages:', err)
      setRestoreError('Unterhaltung konnte nicht geladen werden.')
      return false
    } finally {
      setRestoring(false)
    }
  }, [])

  // Initial Setup
  useEffect(() => {
    if (!token || initRef.current) return
    initRef.current = true

    let storedId: string | null = null
    try {
      storedId = scopedLocalStorage.getItem(storageKey)
    } catch {
      storedId = null
    }

    // TQ #6971: Restore-on-Mount, das den gespeicherten Pointer respektiert.
    // - List-Fehler (null): Pointer NICHT loeschen, History direkt versuchen.
    // - Stored-Session restaurieren (auch wenn jenseits der ersten 100 Sessions).
    // - Fallback auf neueste Session nur, wenn die Stored-Session wirklich weg ist.
    void (async () => {
      const list = await loadSessions()
      if (list === null) {
        if (storedId) {
          setCurrentSessionId(storedId)
          await loadSessionMessages(storedId)
        }
        return
      }
      if (storedId) {
        setCurrentSessionId(storedId)
        const ok = await loadSessionMessages(storedId)
        if (!ok && list[0] && list[0].id !== storedId) {
          setCurrentSessionId(list[0].id)
          await loadSessionMessages(list[0].id)
        }
      } else if (list[0]) {
        setCurrentSessionId(list[0].id)
        await loadSessionMessages(list[0].id)
      } else {
        setCurrentSessionId(undefined)
      }
    })()
  }, [token, storageKey, loadSessions, loadSessionMessages, setCurrentSessionId])

  // ─── Send message (TQ #6093: WS-Stream statt REST) ──────────────
  const sendMessage = useCallback((text: string) => {
    if (!text.trim() || loading || !token) return
    setError(null)
    api.setToken(token)

    const userMessage: Message = { role: 'user', content: text }
    setMessages(prev => [...prev, userMessage])

    setLoading(true)
    setToolStatus('Aria denkt nach…')
    setStreamingContent('')
    streamingContentRef.current = ''

    let resolvedSessionId: string | undefined = currentSessionId
    let didReceiveContent = false

    const stream = api.startCortexChatStream({
      message: text,
      sessionId: currentSessionId,
      onEvent: (evt: CortexStreamEvent) => {
        switch (evt.type) {
          case 'session':
            resolvedSessionId = evt.session_id
            if (!currentSessionId) {
              setCurrentSessionId(evt.session_id)
              void loadSessions()
            }
            setToolStatus('Aria denkt nach…')
            break
          case 'info':
            setToolStatus(evt.message)
            break
          case 'thinking':
            setToolStatus('Aria denkt nach…')
            break
          case 'tool_start':
            if (evt.tool_name === 'tool_search') {
              const query = (evt.args as { query?: string })?.query?.trim()
              setToolStatus(query ? `Sucht Tools für „${query}“…` : 'Sucht passendes Tool…')
            } else {
              setToolStatus(`Rufe ${evt.tool_name} auf…`)
            }
            break
          case 'tool_result':
            setToolStatus(evt.is_error ? `${evt.tool_name}: Fehler` : `${evt.tool_name} fertig`)
            break
          case 'confirmation_required': {
            // TQ #6981: Server hat den Tool-Call geparkt. Bisher gestreamten
            // Content als Bubble promoten, dann eine Confirm-Card-Bubble
            // anhaengen. Die Card ruft approve/cancel.
            const streamed = streamingContentRef.current
            setMessages(prev => {
              const next = [...prev]
              if (streamed.trim()) {
                next.push({ role: 'assistant', content: streamed })
              }
              next.push({
                role: 'assistant',
                content: '',
                pendingConfirm: {
                  tool: evt.tool,
                  reason: evt.reason,
                  instruction: evt.instruction,
                  confirmId: evt.confirm_id,
                },
                confirmState: 'pending',
              })
              return next
            })
            setStreamingContent('')
            streamingContentRef.current = ''
            setToolStatus(null)
            break
          }
          case 'content': {
            if (!didReceiveContent) {
              didReceiveContent = true
              setToolStatus(null)
            }
            const next = streamingContentRef.current + evt.text
            streamingContentRef.current = next
            setStreamingContent(next)
            break
          }
          case 'done': {
            const fullContent = streamingContentRef.current
            if (fullContent) {
              // TQ #6982 (P5): keine ARIA_PREVIEW-Prosa mehr — Write-Confirms
              // laufen ueber das deterministische confirmation_required-Event.
              const finalMsg: Message = {
                role: 'assistant',
                content: fullContent,
              }
              setMessages(prev => [...prev, finalMsg])
            }
            setStreamingContent('')
            streamingContentRef.current = ''
            setToolStatus(null)
            setLoading(false)
            if (resolvedSessionId) {
              setSessions(prev => {
                const sIdx = prev.findIndex(s => s.id === resolvedSessionId)
                if (sIdx === -1) {
                  void loadSessions()
                  return prev
                }
                const updated = { ...prev[sIdx], last_message_at: new Date().toISOString() }
                return [updated, ...prev.filter((_, i) => i !== sIdx)]
              })
            }
            break
          }
          case 'error':
            console.error('[Aria/Cortex stream] error:', evt.message)
            setError(evt.message || 'Verbindungsfehler. Bitte erneut versuchen.')
            setStreamingContent('')
            streamingContentRef.current = ''
            setToolStatus(null)
            setLoading(false)
            setMessages(prev => prev.filter(m => m !== userMessage))
            break
        }
      },
      onClose: (clean: boolean) => {
        streamAbortRef.current = null
        if (loading) {
          if (!clean) {
            setError('Verbindung zur KI-Engine unterbrochen.')
            setMessages(prev => prev.filter(m => m !== userMessage))
          }
          setStreamingContent('')
          streamingContentRef.current = ''
          setToolStatus(null)
          setLoading(false)
        }
      },
    })
    streamAbortRef.current = stream.abort
  }, [loading, token, currentSessionId, setCurrentSessionId, loadSessions])

  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed) return
    setInput('')
    await sendMessage(trimmed)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ─── Confirm-Engine P3 (TQ #6981) ────────────────────────────
  const handleConfirmApprove = useCallback(async (msgIndex: number) => {
    if (!token) return
    api.setToken(token)
    let confirmId: string | undefined
    setMessages(prev => prev.map((m, i) => {
      if (i === msgIndex && m.pendingConfirm) {
        confirmId = m.pendingConfirm.confirmId
        return { ...m, confirmState: 'approving' as const, confirmError: undefined }
      }
      return m
    }))
    if (!confirmId) return
    try {
      const result = await api.approveCortexConfirmation(confirmId)
      setMessages(prev => {
        const next = prev.map((m, i) =>
          i === msgIndex ? { ...m, confirmState: 'approved' as const } : m
        )
        if (result.content && result.content.trim()) {
          next.push({
            role: 'assistant',
            content: result.is_error ? `⚠️ ${result.content}` : result.content,
          })
        }
        return next
      })
    } catch (err) {
      const msg = (err as { error?: string })?.error || 'Bestätigung fehlgeschlagen.'
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, confirmState: 'error' as const, confirmError: msg } : m
      ))
    }
  }, [token])

  const handleConfirmCancel = useCallback(async (msgIndex: number) => {
    if (!token) return
    api.setToken(token)
    let confirmId: string | undefined
    setMessages(prev => {
      const target = prev[msgIndex]
      if (target?.pendingConfirm) confirmId = target.pendingConfirm.confirmId
      return prev.map((m, i) =>
        i === msgIndex ? { ...m, confirmState: 'cancelled' as const, confirmError: undefined } : m
      )
    })
    if (!confirmId) return
    try {
      await api.cancelCortexConfirmation(confirmId)
    } catch (err) {
      // Cancel ist best-effort — Card bleibt 'cancelled', Fehler nur loggen.
      console.error('[Aria/Cortex] cancel confirmation failed:', err)
    }
  }, [token])

  // ─── Session actions ────────────────────────────────────────
  const handleSelectSession = useCallback(async (sessionId: string) => {
    if (sessionId === currentSessionId) return
    setCurrentSessionId(sessionId)
    setInput('')
    setError(null)
    await loadSessionMessages(sessionId)
  }, [currentSessionId, loadSessionMessages, setCurrentSessionId])

  const handleNewSession = useCallback(async () => {
    if (!token) return
    api.setToken(token)
    setError(null)
    setInput('')
    try {
      const created = await api.createCortexSession({})
      setSessions(prev => [created, ...prev.filter(s => s.id !== created.id)])
      setCurrentSessionId(created.id)
      setMessages([])
      setRestoreError(null)
      setTimeout(() => textareaRef.current?.focus(), 50)
    } catch (err) {
      console.error('[Aria/Cortex] Failed to create session:', err)
      setError('Neue Unterhaltung konnte nicht erstellt werden.')
    }
  }, [token, setCurrentSessionId])

  const handleRenameSession = useCallback(async (sessionId: string) => {
    const current = sessions.find(s => s.id === sessionId)
    const proposed = window.prompt('Neuer Titel:', current?.title ?? '')
    if (proposed == null) return
    const trimmed = proposed.trim()
    if (!trimmed) return
    try {
      const updated = await api.renameCortexSession(sessionId, trimmed)
      setSessions(prev => prev.map(s => s.id === sessionId ? updated : s))
    } catch (err) {
      console.error('[Aria/Cortex] Failed to rename session:', err)
      setError('Umbenennen fehlgeschlagen.')
    }
  }, [sessions])

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    const ok = window.confirm('Diese Unterhaltung wirklich löschen?')
    if (!ok) return
    try {
      await api.deleteCortexSession(sessionId)
      setSessions(prev => prev.filter(s => s.id !== sessionId))
      if (sessionId === currentSessionId) {
        const remaining = sessions.filter(s => s.id !== sessionId)
        if (remaining.length > 0) {
          setCurrentSessionId(remaining[0].id)
          await loadSessionMessages(remaining[0].id)
        } else {
          setCurrentSessionId(undefined)
          setMessages([])
        }
      }
    } catch (err) {
      console.error('[Aria/Cortex] Failed to delete session:', err)
      setError('Löschen fehlgeschlagen.')
    }
  }, [currentSessionId, sessions, loadSessionMessages, setCurrentSessionId])

  // TQ #8504: Diese Seite ist reiner Chat. Alle Aria-Einstellungen leben im
  // "Einstellungen"-Bereich (Settings.tsx, Tab-Leiste) — kein Zahnrad mehr.
  return (
    <div className="aria-chat-page aria-page--with-sidebar aria-tabbed-page">
      <div className={`aria-page-layout ${sidebarOpen ? '' : 'aria-page-layout--collapsed'}`}>
        <AriaSessionsSidebar
          sessions={sessions}
          currentSessionId={currentSessionId}
          sessionsLoading={sessionsLoading}
          sessionsError={sessionsError}
          collapsed={!sidebarOpen}
          disabled={!token}
          onSelect={handleSelectSession}
          onNew={handleNewSession}
          onRename={handleRenameSession}
          onDelete={handleDeleteSession}
        />

        <div className="aria-page-main">
          <div className="aria-chat-header">
            <div className="aria-chat-header-left">
              <button
                className="aria-header-icon-btn"
                onClick={() => setSidebarOpen(v => !v)}
                title={sidebarOpen ? 'Sessions verbergen' : 'Sessions anzeigen'}
                aria-label="Sessions umschalten"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div>
                <h1>Aria</h1>
                <p className="aria-chat-subtitle">KI-Assistent für {organization.name}</p>
              </div>
            </div>
          </div>

          <div className="aria-chat-container">
              <div className="aria-chat-messages">
                {restoring && messages.length === 0 && (
                  <div className="aria-bubble aria-bubble--assistant">
                    <div className="aria-bubble-avatar">A</div>
                    <div className="aria-bubble-content aria-typing">
                      <span className="aria-dot" />
                      <span className="aria-dot" />
                      <span className="aria-dot" />
                    </div>
                  </div>
                )}

                {messages.length === 0 && !loading && !restoring && (
                  <div className="aria-chat-welcome">
                    <div className="aria-welcome-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                      </svg>
                    </div>
                    <h2>Hallo! Ich bin Aria.</h2>
                    <p>Ihr KI-Assistent für {organization.name}. Ich habe Zugriff auf die für Ihre Organisation freigegebenen Dienste.</p>
                    <ul className="aria-welcome-examples">
                      <li>„Welche Rechnungen sind diese Woche offen?"</li>
                      <li>„Fasse meine ungelesenen Mails zusammen"</li>
                      <li>„Erstelle eine Rechnung für 3h Beratung"</li>
                    </ul>
                  </div>
                )}

                {messages.map((msg, index) => (
                  <div key={index} className={`aria-bubble aria-bubble--${msg.role}`}>
                    <div className="aria-bubble-avatar">{msg.role === 'user' ? 'Sie' : 'A'}</div>
                    <div className="aria-bubble-content-wrapper">
                      {msg.content && (
                        <div className="aria-bubble-content">
                          {msg.role === 'assistant'
                            ? <MarkdownMessage content={msg.content} />
                            : msg.content}
                        </div>
                      )}
                      {msg.pendingConfirm && msg.confirmState && (
                        <ConfirmCard
                          confirm={msg.pendingConfirm}
                          state={msg.confirmState}
                          error={msg.confirmError}
                          onApprove={() => handleConfirmApprove(index)}
                          onCancel={() => handleConfirmCancel(index)}
                        />
                      )}
                    </div>
                  </div>
                ))}

                {/* TQ #6093: Live-Stream Bubble — wird waehrend content-Events
                    incremental gefuellt, ersetzt nicht mehrere leere Bubbles. */}
                {streamingContent && (
                  <div className="aria-bubble aria-bubble--assistant">
                    <div className="aria-bubble-avatar">A</div>
                    <div className="aria-bubble-content-wrapper">
                      <div className="aria-bubble-content">
                        <MarkdownMessage content={streamingContent} />
                      </div>
                    </div>
                  </div>
                )}

                {/* TQ #6093: Inline Status-Bar — single Element, in-place updates,
                    statt mehrerer leerer 'A' Avatar-Bubbles. */}
                {loading && toolStatus && (
                  <div className="aria-status-bar" role="status" aria-live="polite">
                    <span className="aria-status-spinner" aria-hidden="true">⚙️</span>
                    <span className="aria-status-text">{toolStatus}</span>
                  </div>
                )}
                {loading && !toolStatus && !streamingContent && (
                  <div className="aria-status-bar" role="status" aria-live="polite">
                    <span className="aria-status-text">Aria denkt nach…</span>
                  </div>
                )}

                {error && <div className="aria-chat-error">{error}</div>}
                {restoreError && !error && <div className="aria-chat-error">{restoreError}</div>}

                <div ref={messagesEndRef} />
              </div>

              <div className="aria-chat-input-area">
                <div className="aria-chat-input-wrapper">
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Nachricht an Aria… (Enter = Senden, Shift+Enter = Zeilenumbruch)"
                    disabled={!token}
                    rows={1}
                    className="aria-chat-textarea"
                  />
                  <button
                    className="aria-send-btn"
                    onClick={handleSend}
                    disabled={loading || !input.trim() || !token}
                    title="Senden"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  )
}
